import React from 'react';
import Box from '@mui/material/Box';

/**
 * V2 onboarding shell. Mobile-first 393-CSS-px viewport.
 *
 * Intentionally minimal: provides a white full-height column with horizontal
 * gutters, a flexible content slot, and a bottom-anchored slot for the
 * progress bar. Pages compose their own hero/title/body — the shell does
 * NOT bake in spacing between those, because each onboarding frame in Figma
 * arranges them differently.
 */
export interface OnboardingShellV2Props {
  children: React.ReactNode;
  progress?: React.ReactNode;
  contentSx?: object;
  /**
   * Pin the shell to the viewport height instead of letting the page grow and
   * scroll. In this mode the shell itself never scrolls — a page can hand ONE
   * of its children `flex: 1; minHeight: 0; overflowY: auto` so only that
   * region scrolls while the brand mark, heading, actions and progress bar
   * stay fixed (used by OrganizationPicker so only the org list scrolls).
   */
  fillViewport?: boolean;
}

const OnboardingShellV2: React.FC<OnboardingShellV2Props> = ({
  children,
  progress,
  contentSx,
  fillViewport,
}) => {
  return (
    <Box
      sx={{
        // 100dvh (not 100vh) so the fixed layout tracks the mobile browser's
        // dynamic toolbar instead of hiding the progress bar behind it.
        minHeight: fillViewport ? undefined : '100vh',
        height: fillViewport ? '100dvh' : undefined,
        overflow: fillViewport ? 'hidden' : undefined,
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '480px',
          flex: 1,
          minHeight: fillViewport ? 0 : undefined,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          component="main"
          sx={{
            flex: 1,
            minHeight: fillViewport ? 0 : undefined,
            // The shell never scrolls in fillViewport mode; a child owns the
            // scroll region.
            overflow: fillViewport ? 'hidden' : undefined,
            display: 'flex',
            flexDirection: 'column',
            // Spec: Figma onboarding frames center a 314 px content column
            // in a 393 px viewport → 40 px gutters each side (a_start_* frames).
            paddingInline: '40px',
            ...contentSx,
          }}
        >
          {children}
        </Box>
        {progress && (
          // 24 px of breathing room above the progress bar so it doesn't
          // butt up against the action row on tall pages (e.g. invite/QR).
          // a_start_* frames: progress sits 40 px from frame bottom.
          <Box
            sx={{
              paddingInline: '39px',
              paddingBlockStart: '24px',
              paddingBlockEnd: '40px',
            }}
          >
            {progress}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OnboardingShellV2;
